import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import PasswordValidations from './sections/PasswordValidations/PasswordValidations';
import { ReactComponent as TrashNew } from '../../../../assets/icons/trash-new.svg';
import CustomButton from '../../../../shared/components/Buttons/CustomButton/CustomButton';
import IconButton from '../../../../shared/components/Buttons/IconButton/IconButton';
import TextButton from '../../../../shared/components/Buttons/TextButton/TextButton';
import CustomInput from '../../../../shared/components/Inputs/CustomInput/CustomInput';
import { ARROW_LEFT_ICON, PASSWORD_HIDE_ICON, PASSWORD_SHOW_ICON } from '../../../../shared/constants/images';
import { usePasswordValidation } from '../../../../shared/hooks/usePasswordValidation/usePasswordValidation';
import { TRANSLATIONS_AUTH } from '../../../../shared/translations/auth';

import './CreatePassword.scss';

interface ICreatePassword {
  verificationCode?: string;
  email?: string;
  loading?: boolean;
  onSubmit: (userPassword: string) => void;
  handleGoBack: (newStep: number) => void;
  customError?: string;
  resetCustomError?: () => void;
}

const CreatePassword = ({
  email,
  verificationCode,
  loading = false,
  onSubmit,
  handleGoBack,
  customError,
  resetCustomError
}: ICreatePassword) => {
  const [userPassword, setUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showValidations, setShowValidations] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const buttonText = verificationCode
    ? TRANSLATIONS_AUTH.createPass.updateButton
    : TRANSLATIONS_AUTH.createPass.continueButton;
  const { t } = useTranslation();

  const validation = usePasswordValidation({
    password: userPassword,
    confirmPassword: userPassword
  });

  const validationTexts = () => {
    return {
      description: TRANSLATIONS_AUTH.createPass.passwordValidations.description,
      firstCondition: TRANSLATIONS_AUTH.createPass.passwordValidations.firstCondition,
      secondCondition: TRANSLATIONS_AUTH.createPass.passwordValidations.secondCondition,
      thirdCondition: TRANSLATIONS_AUTH.createPass.passwordValidations.thirdCondition
    };
  };

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (customError && resetCustomError) {
      resetCustomError();
    }
    setUserPassword(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && validation.isValidPassword()) {
      handleSubmit();
    }
  };

  const handleEyeClick = () => {
    setShowPassword(!showPassword);
  };

  const handleTrashClick = () => {
    handleGoBack(0);
  };

  const handleSubmit = () => {
    onSubmit(userPassword);
  };

  const customErrorMessage = customError && userPassword ? t(customError) : '';

  useEffect(() => {
    const shouldShowValidations = userPassword && !validation.isValidPassword();

    if (shouldShowValidations) {
      setShowValidations(true);
      setFadeOut(false);
    } else if (showValidations && !shouldShowValidations) {
      setFadeOut(true);
      const timer = setTimeout(() => {
        setShowValidations(false);
        setFadeOut(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPassword, validation]);

  return (
    <div className="auth__card">
      <div className="auth__go-back">
        <TextButton action={() => handleGoBack(0)} iconLeft={ARROW_LEFT_ICON} variant="primary">
          {t(TRANSLATIONS_AUTH.goBack)}
        </TextButton>
      </div>
      {verificationCode && (
        <div className="create-password__verification-badge">
          <span className="create-password__verification-text">{verificationCode}</span>
          <TrashNew className="create-password__trash-icon" aria-label="Trash Icon" onClick={handleTrashClick} />
        </div>
      )}
      <div className="flex flex-col create-password">
        <div className="create-password__header">
          <div className="text-left create-password__title">{t(TRANSLATIONS_AUTH.createPass.title)}</div>
          {email && <div className="text-left create-password__description">{email}</div>}
        </div>
        <div className="flex flex-col create-password__input-section">
          <CustomInput
            id="createPassword"
            name="createPassword"
            placeholder={t(TRANSLATIONS_AUTH.createPass.placeholder)}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={userPassword}
            onChange={handleChangePassword}
            onKeyDown={handleKeyDown}
            error={customErrorMessage}
            suffix={
              <IconButton
                icon={showPassword ? PASSWORD_HIDE_ICON : PASSWORD_SHOW_ICON}
                action={handleEyeClick}
                variant="transparent"
                size="s"
                label="Toggle password visibility"
              />
            }
            containerClassName="auth__input"
          />
          {showValidations && (
            <div className={`create-password__validations ${fadeOut ? 'create-password__validations--fade-out' : ''}`}>
              <PasswordValidations validation={validation} texts={validationTexts()} />
            </div>
          )}
        </div>

        <div>
          <CustomButton
            variant="primary"
            size="l"
            onClick={handleSubmit}
            disabled={!validation.isValidPassword() || loading}
            loading={loading}
          >
            {t(buttonText)}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default CreatePassword;
